<!--
    Org-Part Tree Builder — shared template for the Edit-Position dialog and
    the Bulk-Create-Position dialog. Per-launch wording, icons, and behaviour
    flags come from `config: BuilderConfig`. The component owns the working
    `tree: BuilderTreeNode[]` and `flatRows`, plus all save/task-monitor state.
-->
<div class="builder">
    <!-- Header bar -->
    <div class="builder-header">
        <div class="header-left">
            <mat-icon class="header-icon">{{ config.headerIcon }}</mat-icon>
            <span class="header-title">{{ config.headerTitle }}</span>
            @if (config.headerSubtitle) {
                <span class="header-meta">{{ config.headerSubtitle }}</span>
            }
        </div>
        <div class="header-right">
            @if (dirtyCount > 0) {
                <span class="counter modified">{{ dirtyCount }} modified</span>
            }
            @if (newCount > 0) {
                <span class="counter new-count">{{ newCount }} new</span>
            }
            @if (movedCount > 0) {
                <span class="counter moved-count">{{ movedCount }} moved</span>
            }
            @if (deletedCount > 0) {
                <span class="counter deleted-count">{{ deletedCount }} deleted</span>
            }

            <!-- Undo / redo (dialog mode only — the wizard owns its own) -->
            @if (!config.autoSave) {
                <button
                    mat-icon-button
                    class="toolbar-btn"
                    [disabled]="!canUndo"
                    (click)="undo()"
                    matTooltip="Undo (Ctrl+Z)"
                >
                    <mat-icon>undo</mat-icon>
                </button>
                <button
                    mat-icon-button
                    class="toolbar-btn"
                    [disabled]="!canRedo"
                    (click)="redo()"
                    matTooltip="Redo (Ctrl+Shift+Z)"
                >
                    <mat-icon>redo</mat-icon>
                </button>
            }

            <!-- Add Root (bulk only) -->
            @if (config.showAddRootButton) {
                <button
                    mat-stroked-button
                    color="primary"
                    class="add-root-btn"
                    [disabled]="isAtLimit"
                    (click)="addRootNode()"
                    [matTooltip]="
                        isAtLimit ? 'Maximum ' + config.maxPositions + ' positions reached' : 'Add a top-level position'
                    "
                >
                    <mat-icon>add</mat-icon>
                    Add Root
                </button>
            }

            <!-- Field-toggle buttons -->
            <button
                mat-icon-button
                class="toolbar-btn field-toggle"
                [class.is-active]="showField.colour"
                (click)="showField.colour = !showField.colour"
                [matTooltip]="(showField.colour ? 'Hide' : 'Show') + ' colour swatch'"
            >
                <mat-icon>palette</mat-icon>
            </button>
            <button
                mat-icon-button
                class="toolbar-btn field-toggle"
                [class.is-active]="showField.typePill"
                (click)="showField.typePill = !showField.typePill"
                [matTooltip]="(showField.typePill ? 'Hide' : 'Show') + ' type label'"
            >
                <mat-icon>label</mat-icon>
            </button>
            <button
                mat-icon-button
                class="toolbar-btn field-toggle"
                [class.is-active]="showField.name"
                (click)="showField.name = !showField.name"
                [matTooltip]="(showField.name ? 'Hide' : 'Show') + ' name'"
            >
                <mat-icon>title</mat-icon>
            </button>
            <button
                mat-icon-button
                class="toolbar-btn field-toggle"
                [class.is-active]="showField.subtitle"
                (click)="showField.subtitle = !showField.subtitle"
                [matTooltip]="(showField.subtitle ? 'Hide' : 'Show') + ' subtitle'"
            >
                <mat-icon>short_text</mat-icon>
            </button>
            <button
                mat-icon-button
                class="toolbar-btn field-toggle"
                [class.is-active]="showField.type"
                (click)="showField.type = !showField.type"
                [matTooltip]="(showField.type ? 'Hide' : 'Show') + ' type selector'"
            >
                <mat-icon>category</mat-icon>
            </button>
            <button
                mat-icon-button
                class="toolbar-btn field-toggle"
                [class.is-active]="showField.headerRole"
                (click)="showField.headerRole = !showField.headerRole"
                [matTooltip]="(showField.headerRole ? 'Hide' : 'Show') + ' header role'"
            >
                <mat-icon>military_tech</mat-icon>
            </button>
            <button
                mat-icon-button
                class="toolbar-btn field-toggle"
                [class.is-active]="showField.vfp"
                (click)="showField.vfp = !showField.vfp"
                [matTooltip]="(showField.vfp ? 'Hide' : 'Show') + ' VFP'"
            >
                <mat-icon>flag</mat-icon>
            </button>
            <button
                mat-icon-button
                class="toolbar-btn collapse-all-btn"
                (click)="toggleCollapseAll()"
                [matTooltip]="allCollapsed ? 'Expand all' : 'Collapse all'"
            >
                <mat-icon>{{ allCollapsed ? 'unfold_more' : 'unfold_less' }}</mat-icon>
            </button>
            <button mat-icon-button class="builder-close-btn" (click)="onCancel()" matTooltip="Close">
                <mat-icon>close</mat-icon>
            </button>
        </div>
    </div>

    <!-- Org breadcrumb -->
    @if (config.breadcrumb.length > 0) {
        <div class="org-breadcrumb">
            <mat-icon class="breadcrumb-icon">location_on</mat-icon>
            @for (item of config.breadcrumb; track $index; let last = $last) {
                <span class="breadcrumb-item" [class.breadcrumb-current]="last && !config.showInsertionPointChip">
                    <span class="breadcrumb-type">{{ item.partType }}</span>
                    <span class="breadcrumb-name">{{ item.name }}</span>
                </span>
                @if (!last || config.showInsertionPointChip) {
                    <mat-icon class="breadcrumb-sep">chevron_right</mat-icon>
                }
            }
            @if (config.showInsertionPointChip) {
                <span class="breadcrumb-item breadcrumb-new">
                    <mat-icon class="inline-icon">{{
                        config.insertionType === 'sibling' ? 'unfold_more' : 'subdirectory_arrow_right'
                    }}</mat-icon>
                    <span class="breadcrumb-type">{{
                        config.insertionType === 'sibling' ? 'siblings' : 'children'
                    }}</span>
                </span>
            }
        </div>
    }

    <!-- Main content area -->
    <div class="content-layout">
        <!-- Left: tree panel -->
        <div class="tree-panel">
            <div class="builder-tree">
                <div
                    class="flat-tree-list"
                    cdkDropList
                    cdkDropListAutoScrollDisabled
                    cdkDropListSortingDisabled
                    (cdkDropListDropped)="onFlatDrop()"
                >
                    @for (row of flatRows; track trackFlatRow($index, row)) {
                        <div
                            class="flat-tree-row"
                            [style.padding-left.px]="row.depth * 28"
                            [attr.data-part-key]="row.node.partKey"
                            [class.is-dirty]="row.node.dirty && !row.node.isNew"
                            [class.is-new]="row.node.isNew"
                            [class.is-moved]="row.node.moved"
                            [class.is-root]="isProtectedRoot(row.node)"
                            [class.is-board-seat]="isBoardSeat(row)"
                            [class.is-linked]="isLinked(row.node)"
                            [class.is-being-dragged]="draggedNode?.partKey === row.node.partKey"
                            [class.pending-delete]="pendingDeleteNode === row.node"
                            cdkDrag
                            [cdkDragData]="row.node"
                            [cdkDragDisabled]="isProtectedRoot(row.node) || isLinked(row.node)"
                            (cdkDragStarted)="onDragStarted(row.node)"
                            (cdkDragMoved)="onDragMoved($event)"
                            (cdkDragEnded)="onDragEnded()"
                        >
                            <!-- Drag handle -->
                            @if (!isProtectedRoot(row.node) && !isLinked(row.node)) {
                                <div class="drag-handle" cdkDragHandle>
                                    <mat-icon>drag_indicator</mat-icon>
                                </div>
                            }

                            <!-- Collapse toggle -->
                            @if (hasChildren(row.node)) {
                                <button mat-icon-button class="collapse-btn" (click)="toggleCollapse(row.node)">
                                    <mat-icon>{{ row.node.collapsed ? 'chevron_right' : 'expand_more' }}</mat-icon>
                                </button>
                            } @else {
                                <div class="collapse-placeholder"></div>
                            }

                            <!-- Status indicators -->
                            <div
                                class="status-indicators"
                                [matTooltip]="getStatusTooltip(row.node)"
                                [matTooltipDisabled]="!row.node.isNew && !row.node.moved && !row.node.dirty"
                            >
                                @if (row.node.isNew) {
                                    <span class="status-indicator is-new">
                                        <mat-icon>add_circle</mat-icon>
                                    </span>
                                }
                                @if (row.node.moved) {
                                    <span class="status-indicator is-moved">
                                        <mat-icon>swap_vert</mat-icon>
                                    </span>
                                }
                                @if (row.node.dirty && !row.node.isNew) {
                                    <span class="status-indicator is-dirty">
                                        <mat-icon>edit</mat-icon>
                                    </span>
                                }
                            </div>

                            <!-- Colour swatch -->
                            @if (showField.colour) {
                                <div
                                    class="colour-swatch"
                                    [style.background-color]="row.node.colour.background"
                                    [style.border-color]="row.node.colour.color"
                                    [matMenuTriggerFor]="colourMenu"
                                    matTooltip="Edit colours"
                                ></div>
                            }
                            <mat-menu #colourMenu="matMenu" class="colour-menu">
                                <div class="colour-picker-panel" (click)="$event.stopPropagation()">
                                    <label
                                        >Background
                                        <input
                                            type="color"
                                            [ngModel]="row.node.colour.background"
                                            (ngModelChange)="row.node.colour.background = $event; markDirty(row.node)"
                                        />
                                    </label>
                                    <label
                                        >Text
                                        <input
                                            type="color"
                                            [ngModel]="row.node.colour.color"
                                            (ngModelChange)="row.node.colour.color = $event; markDirty(row.node)"
                                        />
                                    </label>
                                    <label
                                        >Link
                                        <input
                                            type="color"
                                            [ngModel]="row.node.colour.linkColor"
                                            (ngModelChange)="row.node.colour.linkColor = $event; markDirty(row.node)"
                                        />
                                    </label>
                                </div>
                            </mat-menu>

                            <!-- Type pill -->
                            @if (showField.typePill) {
                                <span
                                    class="part-type-pill"
                                    [attr.data-type]="
                                        isBoardSeat(row) ? 'board' : isLinked(row.node) ? 'linked' : row.node.partType
                                    "
                                    [title]="
                                        isBoardSeat(row)
                                            ? 'post — Board of Directors box'
                                            : isLinked(row.node)
                                              ? 'Represents ' + linkedTargetName(row.node) + ' — edit it there'
                                              : row.node.partType
                                    "
                                >
                                    {{ isBoardSeat(row) ? 'board' : isLinked(row.node) ? 'linked' : row.node.partType }}
                                </span>
                            }

                            <!-- CEO crown (post-only, opt-in via config hooks) — addendum 19 -->
                            @if (config.isPostCrowned && config.onTogglePostCrown && row.node.partType === 'post') {
                                <button
                                    mat-icon-button
                                    type="button"
                                    class="ceo-crown-btn"
                                    [class.is-crowned]="config.isPostCrowned(row.node.partKey)"
                                    [matTooltip]="
                                        config.isPostCrowned(row.node.partKey)
                                            ? 'CEO post — click another post to move the crown'
                                            : 'Mark as CEO post'
                                    "
                                    (click)="config.onTogglePostCrown(row.node.partKey)"
                                >
                                    <span class="ceo-crown-glyph" aria-hidden="true">👑</span>
                                </button>
                            }

                            <!-- Name -->
                            @if (showField.name) {
                                <mat-form-field appearance="outline" class="field-name" subscriptSizing="dynamic">
                                    <input
                                        matInput
                                        [(ngModel)]="row.node.name"
                                        (ngModelChange)="markDirty(row.node)"
                                        [disabled]="isLinked(row.node)"
                                        placeholder="Name"
                                    />
                                </mat-form-field>
                            }

                            <!-- Subtitle (hidden for new nodes when configured) -->
                            @if (showSubtitleField(row.node)) {
                                <mat-form-field appearance="outline" class="field-subtitle" subscriptSizing="dynamic">
                                    <input
                                        matInput
                                        [(ngModel)]="row.node.subTitle"
                                        (ngModelChange)="markDirty(row.node)"
                                        [disabled]="isLinked(row.node)"
                                        placeholder="Subtitle"
                                    />
                                </mat-form-field>
                            }

                            <!-- Type selector (allowed-children aware) -->
                            @if (showField.type) {
                                <mat-form-field appearance="outline" class="field-type" subscriptSizing="dynamic">
                                    <mat-select
                                        [(ngModel)]="row.node.partType"
                                        (ngModelChange)="markDirty(row.node)"
                                        [disabled]="isLinked(row.node)"
                                    >
                                        @for (type of typeOptionsFor(row); track type) {
                                            <mat-option [value]="type">{{ type }}</mat-option>
                                        }
                                    </mat-select>
                                </mat-form-field>
                            }

                            <!-- Header role (post-only, never on protected root) -->
                            @if (showField.headerRole && row.node.partType === 'post' && !isProtectedRoot(row.node)) {
                                <mat-form-field
                                    appearance="outline"
                                    class="field-header-role"
                                    subscriptSizing="dynamic"
                                    matTooltip="Show this post inside the parent container's header strip"
                                >
                                    <mat-select
                                        [(ngModel)]="row.node.headerRole"
                                        (ngModelChange)="markDirty(row.node)"
                                        [disabled]="isLinked(row.node)"
                                        placeholder="Header role"
                                    >
                                        <mat-option [value]="''">— Body —</mat-option>
                                        <mat-option value="head">Head</mat-option>
                                        <mat-option value="deputy">Deputy</mat-option>
                                        <mat-option value="assistant">Assistant</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            }

                            <!-- Linked post: it REPRESENTS another post and comes from there, so the row is
                                 read-only — only its origin is shown (Paul, 2026-10-21). -->
                            @if (isLinked(row.node)) {
                                <span class="linked-origin" [title]="'Edit ' + linkedTargetName(row.node) + ' instead'">
                                    <mat-icon class="linked-origin-icon">link</mat-icon>
                                    Represents {{ linkedTargetName(row.node) }}
                                </span>
                            }

                            <!-- Detached-group placement: only a post directly in the board root
                                 (the CEO's peers) may be placed in the floating Board of Directors
                                 box, and pick the division its cosmetic line points at. Entering the
                                 group defaults the post out of Held-From-Above. -->
                            @if (showField.headerRole && canPlaceInDetachedGroup(row)) {
                                <mat-form-field
                                    appearance="outline"
                                    class="field-detached-group"
                                    subscriptSizing="dynamic"
                                    matTooltip="Render this post in its own floating box (e.g. the Board of Directors beside the CEO) instead of inside its container"
                                >
                                    <mat-select
                                        [(ngModel)]="row.node.detachedGroup"
                                        (ngModelChange)="onDetachedGroupChange(row.node)"
                                        placeholder="Placement"
                                    >
                                        <mat-option [value]="''">— On the board —</mat-option>
                                        <mat-option value="board">Board of Directors box</mat-option>
                                    </mat-select>
                                </mat-form-field>
                                @if (row.node.detachedGroup) {
                                    <mat-form-field
                                        appearance="outline"
                                        class="field-connects-to"
                                        subscriptSizing="dynamic"
                                        matTooltip="Division the box draws its connector line down to (cosmetic only)"
                                    >
                                        <mat-select
                                            [(ngModel)]="row.node.detachedGroupConnectsTo"
                                            (ngModelChange)="markDirty(row.node)"
                                            placeholder="Line to division"
                                        >
                                            <mat-option [value]="''">— No line —</mat-option>
                                            @for (d of divisionOptions; track d.partKey) {
                                                <mat-option [value]="d.partKey">{{ d.name }}</mat-option>
                                            }
                                        </mat-select>
                                    </mat-form-field>
                                }
                            }

                            <!-- VFP -->
                            @if (showField.vfp && !isLinked(row.node)) {
                                <mat-form-field
                                    appearance="outline"
                                    class="field-vfp"
                                    subscriptSizing="dynamic"
                                    matTooltip="Valuable Final Product (drag the bottom-right corner to make the editor taller)"
                                >
                                    <textarea
                                        matInput
                                        rows="1"
                                        [(ngModel)]="row.node.vfp"
                                        (ngModelChange)="markDirty(row.node)"
                                        placeholder="VFP"
                                    ></textarea>
                                </mat-form-field>
                            }

                            <!-- Developer fields (id / depth) -->
                            @if (isDeveloper && !row.node.isNew) {
                                <span class="dev-field-inline" matTooltip="ID">#{{ row.node.id }}</span>
                                <span class="dev-field-inline" matTooltip="Depth">d{{ row.node.depth }}</span>
                            }

                            <!-- Node actions (none on a linked post: it is edited at its origin) -->
                            <div class="node-actions">
                                @if (!isLinked(row.node)) {
                                    @if (!isLeafType(row.node.partType)) {
                                        <button
                                            mat-icon-button
                                            matTooltip="Add child"
                                            (click)="addChild(row.node)"
                                            color="primary"
                                            [disabled]="isAtLimit"
                                        >
                                            <mat-icon>subdirectory_arrow_right</mat-icon>
                                        </button>
                                    }
                                    @if (!isProtectedRoot(row.node) && row.parentNode) {
                                        <button
                                            mat-icon-button
                                            matTooltip="Insert sibling below"
                                            (click)="addSibling(row.node, row.parentNode.children)"
                                            color="accent"
                                            [disabled]="isAtLimit"
                                        >
                                            <mat-icon>add</mat-icon>
                                        </button>
                                    }
                                    @if (!isProtectedRoot(row.node) && row.parentNode === null) {
                                        <!-- Top-level (root) sibling insert — bulk multi-root only -->
                                        @if (config.minDepth === 0) {
                                            <button
                                                mat-icon-button
                                                matTooltip="Insert sibling below"
                                                (click)="addSibling(row.node, tree)"
                                                color="accent"
                                                [disabled]="isAtLimit"
                                            >
                                                <mat-icon>add</mat-icon>
                                            </button>
                                        }
                                    }
                                    @if (!isProtectedRoot(row.node)) {
                                        @if (pendingDeleteNode === row.node) {
                                            <button
                                                mat-icon-button
                                                matTooltip="Confirm delete"
                                                color="warn"
                                                (click)="
                                                    executeDelete(
                                                        row.node,
                                                        row.parentNode ? row.parentNode.children : tree
                                                    )
                                                "
                                            >
                                                <mat-icon>check</mat-icon>
                                            </button>
                                            <button mat-icon-button matTooltip="Cancel" (click)="cancelDelete()">
                                                <mat-icon>close</mat-icon>
                                            </button>
                                        } @else {
                                            <button
                                                mat-icon-button
                                                matTooltip="Remove"
                                                color="warn"
                                                (click)="
                                                    confirmDelete(
                                                        row.node,
                                                        row.parentNode ? row.parentNode.children : tree,
                                                        $event
                                                    )
                                                "
                                            >
                                                <mat-icon>delete_outline</mat-icon>
                                            </button>
                                        }
                                    }
                                }
                            </div>

                            <!-- Drag preview -->
                            <div *cdkDragPreview class="drag-preview">
                                <mat-icon>drag_indicator</mat-icon>
                                {{ row.node.name || 'Unnamed' }} ({{ row.node.partType }})
                                @if (descendantCount(row.node) > 0) {
                                    <span
                                        class="drag-preview-count"
                                        matTooltip="This branch and everything under it moves together"
                                    >
                                        +{{ descendantCount(row.node) }}
                                    </span>
                                }
                            </div>
                        </div>

                        <!-- Delete-warning banner -->
                        @if (pendingDeleteNode === row.node) {
                            <div class="delete-warning" [style.padding-left.px]="row.depth * 28 + 24">
                                <mat-icon>warning</mat-icon>
                                <span
                                    >Delete <strong>{{ row.node.name || 'this part' }}</strong>
                                    @if (row.node.children.length > 0) {
                                        and {{ row.node.children.length }} child{{
                                            row.node.children.length !== 1 ? 'ren' : ''
                                        }}
                                    }
                                    ? Click <mat-icon class="inline-icon">check</mat-icon> to confirm.
                                </span>
                            </div>
                        }
                    }

                    <!-- Empty-tree placeholder (bulk multi-root mode only) -->
                    @if (flatRows.length === 0) {
                        <div class="empty-state">
                            <mat-icon>playlist_add</mat-icon>
                            <p>No positions yet — click <strong>Add Root</strong> to start.</p>
                        </div>
                    }

                    <!-- Live drop indicator -->
                    @if (dropIntent && draggedNode) {
                        @if (dropIntent.zoneHeight > 0 && depthZoneRange().length > 1) {
                            <div
                                class="depth-zones"
                                [style.top.px]="dropIntent.zoneTop"
                                [style.height.px]="dropIntent.zoneHeight"
                            >
                                @for (d of depthZoneRange(); track d) {
                                    <div
                                        class="depth-zone"
                                        [class.active]="d === dropIntent.depth"
                                        [attr.data-depth]="displayDepth(d)"
                                        [style.left.px]="d * 28"
                                        [style.width.px]="28"
                                    >
                                        <span class="depth-zone-label">{{ displayDepth(d) }}</span>
                                    </div>
                                }
                            </div>
                        }
                        <div
                            class="drop-indicator"
                            [class.invalid]="!dropIntent.valid"
                            [attr.data-depth]="displayDepth(dropIntent.depth)"
                            [style.top.px]="dropIntent.indicatorTop"
                            [style.left.px]="dropIntent.depth * 28"
                        >
                            <span class="indicator-label">
                                @if (dropIntent.valid) {
                                    @if (dropIntent.targetParent; as parent) {
                                        Child of <span class="indicator-type">{{ parent.partType }}</span>
                                        <strong>{{ parent.name || 'Unnamed' }}</strong>
                                    } @else {
                                        Child of <strong>root</strong>
                                    }
                                    <span class="depth-pill">depth {{ displayDepth(dropIntent.depth) }}</span>
                                } @else {
                                    {{ dropIntent.invalidReason }}
                                }
                            </span>
                        </div>
                    }
                </div>
            </div>

            <!-- Developer Info (edit-mode + developer only) -->
            @if (config.showDeveloperInfo && isDeveloper && tree.length === 1) {
                <div class="dev-section">
                    <button mat-button class="dev-toggle" (click)="devExpanded = !devExpanded">
                        <mat-icon>{{ devExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                        Developer Info
                    </button>
                    @if (devExpanded) {
                        <div class="dev-details">
                            <div class="dev-row">
                                <span class="dev-label">Part Key</span>
                                <code>{{ tree[0].partKey }}</code>
                            </div>
                            <div class="dev-row">
                                <span class="dev-label">Parent Part Key</span>
                                <code>{{ tree[0].parentPartKey }}</code>
                            </div>
                            <div class="dev-row">
                                <span class="dev-label">Path</span>
                                <code>{{ tree[0].path }}</code>
                            </div>
                            @if (tree[0].vfp) {
                                <div class="dev-row">
                                    <span class="dev-label">VFP</span>
                                    <code>{{ tree[0].vfp }}</code>
                                </div>
                            }
                        </div>
                    }
                </div>
            }
        </div>

        <!-- Right: changes sidebar (hidden in autoSave mode — the wizard's
             top-bar Undo/Redo replaces the explicit pending-changes panel) -->
        @if (!config.autoSave) {
            <div class="changes-panel">
                <div class="changes-panel-header">
                    <mat-icon>list_alt</mat-icon>
                    <span>Pending Changes</span>
                </div>
                <div class="changes-panel-body">
                    @if (savePhase === 'processing' || savePhase === 'done') {
                        @for (task of taskStatuses; track task.key) {
                            <div class="change-item {{ task.action }}">
                                <mat-icon class="change-icon {{ task.action }}">{{ task.icon }}</mat-icon>
                                <span class="change-detail">
                                    <strong>{{ task.name }}</strong>
                                    <span class="change-action-label">{{ task.action }}</span>
                                </span>
                                <mat-icon class="status-icon" [class]="task.status">{{ task.statusIcon }}</mat-icon>
                            </div>
                        }
                        @if (taskStatuses.length === 0) {
                            <div class="no-changes">
                                <mat-spinner diameter="24"></mat-spinner>
                                <span>Waiting for tasks...</span>
                            </div>
                        }
                    } @else if (!hasChanges && savePhase === 'idle') {
                        <div class="no-changes">
                            <mat-icon>check_circle_outline</mat-icon>
                            <span>No changes yet</span>
                            <p>Edit fields, add or remove parts, and drag to reorder.</p>
                        </div>
                    } @else {
                        @for (change of changesSummary; track change.key) {
                            <div class="change-item" [class]="change.action">
                                <mat-icon class="change-icon" [class]="change.action">{{ change.icon }}</mat-icon>
                                <span>
                                    <strong>{{ change.name }}</strong>
                                    <span class="change-action-label">{{ change.label }}</span>
                                </span>
                            </div>
                        }
                    }
                </div>

                <!-- Save progress -->
                @if (savePhase === 'writing') {
                    <div class="save-progress">
                        <mat-progress-bar
                            mode="determinate"
                            [value]="(saveProgress / totalChanges) * 100"
                        ></mat-progress-bar>
                        <span>Writing changes... {{ saveProgress }} / {{ totalChanges }}</span>
                    </div>
                }
                @if (savePhase === 'processing') {
                    <div class="save-progress">
                        <mat-progress-bar
                            mode="determinate"
                            [value]="totalTasks > 0 ? (tasksCompleted / totalTasks) * 100 : 0"
                        ></mat-progress-bar>
                        <span>Processing tasks... {{ tasksCompleted }} / {{ totalTasks }}</span>
                    </div>
                }
                @if (savePhase === 'done') {
                    <div class="save-progress done">
                        <mat-icon>check_circle</mat-icon>
                        <span>All tasks completed!</span>
                    </div>
                }
                @if (savePhase === 'error') {
                    <div class="save-progress error">
                        <mat-icon>error</mat-icon>
                        <span>Save failed. Please try again.</span>
                    </div>
                }
            </div>
        }
    </div>

    <!-- Footer (hidden in autoSave mode — every edit auto-flushes to the host) -->
    @if (!config.autoSave) {
        <div class="builder-footer">
            @if (savePhase === 'done') {
                @if (config.refreshTree) {
                    <button mat-button (click)="continueEditing()">
                        <mat-icon>edit</mat-icon>
                        Continue Editing
                    </button>
                }
                <button mat-raised-button color="primary" (click)="onCloseAfterSave()">
                    <mat-icon>check</mat-icon>
                    Close
                </button>
            } @else {
                <button mat-button (click)="onCancel()" [disabled]="isSaving">Cancel</button>
                <button mat-raised-button color="accent" [disabled]="isSaving || !hasChanges" (click)="onSave()">
                    @if (isSaving) {
                        <mat-spinner diameter="18" class="button-spinner"></mat-spinner>
                    } @else {
                        <mat-icon>save</mat-icon>
                    }
                    {{ config.saveButtonLabel }} ({{ totalPendingChanges }})
                </button>
            }
        </div>
    }
</div>
